import { useCallback, useEffect, useState } from 'react';
import {
  createMetric,
  deleteMetric,
  listMetrics,
  updateMetric,
} from '../api/metrics.js';
import type {
  CreateMetricRequest,
  MetricEntry,
  UpdateMetricRequest,
} from '../api/metricsTypes.js';
import { ApiError } from '../api/client.js';

export interface UseMetricsResult {
  entries: MetricEntry[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  create: (req: CreateMetricRequest) => Promise<MetricEntry>;
  update: (id: number, req: UpdateMetricRequest) => Promise<MetricEntry>;
  remove: (id: number) => Promise<void>;
}

export function useMetrics(): UseMetricsResult {
  const [entries, setEntries] = useState<MetricEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const list = await listMetrics();
      setEntries(list);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Failed to load metrics';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const create = useCallback(
    async (req: CreateMetricRequest): Promise<MetricEntry> => {
      const created = await createMetric(req);
      setEntries((prev) => [...prev, created]);
      return created;
    },
    [],
  );

  const update = useCallback(
    async (id: number, req: UpdateMetricRequest): Promise<MetricEntry> => {
      const updated = await updateMetric(id, req);
      setEntries((prev) => prev.map((e) => (e.id === id ? updated : e)));
      return updated;
    },
    [],
  );

  const remove = useCallback(async (id: number): Promise<void> => {
    await deleteMetric(id);
    setEntries((prev) => prev.filter((e) => e.id !== id));
  }, []);

  return { entries, loading, error, refresh, create, update, remove };
}
